import { CSSProperties } from "react";
import { ButtonColorType, ButtonSizeType } from "../types";

type args = {
  color: ButtonColorType;
  disabled?: boolean;
  fullWidth?: boolean;
  isFocused: boolean;
  isHovered: boolean;
  size?: ButtonSizeType;
};

export default function getButtonStyles({
  color,
  disabled,
  fullWidth,
  isFocused,
  isHovered,
  size,
}: args): CSSProperties {
  const hasTouchLikePrimaryInput =
    typeof window !== "undefined" &&
    window.matchMedia("(hover: none), (pointer: coarse)").matches;

  const shouldApplyInteractiveState = !hasTouchLikePrimaryInput;

  const effectiveIsHovered = shouldApplyInteractiveState && isHovered;
  const effectiveIsFocused = shouldApplyInteractiveState && isFocused;

  const fullWidthPadding =
    size === "lg"
      ? "0 var(--spacing-xl)"
      : size === "sm"
        ? "0 var(--spacing-md)"
        : size === "xs"
          ? "0 var(--spacing-sm)"
          : "0 var(--spacing-lg)";

  const autoWidthPadding =
    size === "lg"
      ? "0 var(--spacing-5xl)"
      : size === "sm"
        ? "0 var(--spacing-lg)"
        : size === "xs"
          ? "0 var(--spacing-md)"
          : "0 var(--spacing-3xl)";

  const border =
    color !== "outlined" && color !== "danger"
      ? "none"
      : disabled
        ? `var(--border-width-xs) solid var(--color-button-border-disabled-${color})`
        : effectiveIsHovered
          ? `var(--border-width-md) solid var(--color-button-border-hover-${color})`
          : effectiveIsFocused
            ? `var(--border-width-md) solid var(--color-button-border-focus-${color})`
            : `var(--border-width-md) solid var(--color-button-border-${color})`;

  const borderRadius =
    size === "sm" || size === "xs"
      ? "var(--border-radius-sm)"
      : "var(--border-radius-md)";

  const backgroundColor = disabled
    ? `var(--color-button-bg-disabled-${color})`
    : effectiveIsHovered
      ? `var(--color-button-bg-hover-${color})`
      : effectiveIsFocused
        ? `var(--color-button-bg-focus-${color})`
        : `var(--color-button-bg-${color})`;

  const textColor = disabled
    ? `var(--color-button-text-disabled-${color})`
    : effectiveIsHovered
      ? `var(--color-button-text-hover-${color})`
      : effectiveIsFocused
        ? `var(--color-button-text-focus-${color})`
        : `var(--color-button-text-${color})`;

  const height =
    size === "lg"
      ? "3.5rem"
      : size === "sm"
        ? "2.5rem"
        : size === "xs"
          ? "2rem"
          : "3rem";

  const fontSize =
    size === "sm"
      ? "var(--font-size-xs)"
      : size === "xs"
        ? "var(--font-size-2xs)"
        : "var(--font-size-sm)";

  return {
    border: border,
    borderRadius: borderRadius,

    minWidth: 0,
    maxWidth: "100%",
    width: fullWidth ? "100%" : "fit-content",
    height: height,
    lineHeight: height,

    padding: fullWidth ? fullWidthPadding : autoWidthPadding,

    backgroundColor: backgroundColor,
    color: textColor,

    fontSize: fontSize,
    fontWeight: 600,
    textAlign: "center",

    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };
}
